import Vue from 'vue'
import Router from 'vue-router'
import shipperUiRoutes from './shipperUi'
import receiverUiRoutes from './receiverUi'
import sysAdminUiRoutes from './sysAdminUi'

// ------------------------------------------- Auth & Account Registration Pages
import OAuthHandler from '../components/auth/OAuthHandler.vue'
import LoginPage from '../pages/public/LoginPage.vue'
import ForgotPasswordPage from '../pages/public/ForgotPasswordPage.vue'
import ConfirmChangePasswordPage from '../pages/public/ConfirmChangePasswordPage.vue'
import RegisterAccountTypeSelectionPage from '../pages/public/RegisterAccountTypeSelectionPage.vue'
import RegisterShipperAccountPage from '../pages/public/RegisterShipperAccountPage.vue'
import ClaimOrgAccountPage from '../pages/public/ClaimOrgAccountPage.vue'
import AcceptMemberInvitePage from '../pages/public/AcceptMemberInvitePage.vue'

// ----------------------------------------------------------- Terms of Use Page
import TermsOfUsePage from '../pages/public/TermsOfUsePage.vue'

// ------------------------------------------------------------------- Home Page
import HomePage from '../pages/home/HomePage.vue'

// ------------------------------------------------------------ Track Order Page
import TrackOrderPage from '../pages/public/TrackOrderPage.vue'

// --------------------------------------------------------- User Settings Pages
import UserSettingsRootPage from '../pages/account/settings/UserSettingsRootPage.vue'
import UserSettingsProfile from '../pages/account/settings/UserSettingsProfile.vue'
import UserSettingsFacilityAgent from '../pages/account/settings/UserSettingsFacilityAgent.vue'
import UserSettingsDriver from '../pages/account/settings/UserSettingsDriver.vue'

// -------------------------------------------------------------- Download Pages
import DownloadOrderInvoicePage from '../pages/download/DownloadOrderInvoicePage.vue'
import DownloadRouteManifestPage from '../pages/download/DownloadRouteManifestPage.vue'

// ----------------------------------------------------------- Org Profile Pages
import OrgProfilePage from '../pages/orgProfile/OrgProfilePage.vue'
import OrgProfileEditPage from '../pages/orgProfile/OrgProfileEditPage.vue'

// ------------------------------------------------------------ Org Member Pages
import UserListPage from '../pages/orgMembers/UserListPage.vue'
import UserRootPage from '../pages/orgMembers/UserRootPage.vue'
import UserItemPage from '../pages/orgMembers/UserItemPage.vue'

// ----------------------------------------------------- Receiver Location Pages
import ReceiverListPage from '../pages/admin/receivers/ReceiverListPage.vue'
import ReceiverRootPage from '../pages/admin/receivers/ReceiverRootPage.vue'
import ReceiverItemPage from '../pages/admin/receivers/ReceiverItemPage.vue'
import ReceiverItemEditDetailsPage from '../pages/admin/receivers/ReceiverItemEditDetailsPage.vue'
import ReceiverItemEditLicensesPage from '../pages/admin/receivers/ReceiverItemEditLicensesPage.vue'
import ReceiverLicenseChangeHistoryPage from '../pages/admin/receivers/ReceiverLicenseChangeHistoryPage.vue'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  scrollBehavior: () => ({ y: 0 }),
  base: process.env.BASE_URL || window.location.host,

  routes: [
    // ---------------------------------------------------- Account Registration
    {
      path: '/register',
      name: 'register-account-type-select',
      component: RegisterAccountTypeSelectionPage
    },
    {
      path: '/register-shipper-account',
      name: 'register-shipper-account',
      component: RegisterShipperAccountPage
    },
    {
      path: '/claim-account',
      name: 'claim-account',
      component: ClaimOrgAccountPage
    },
    {
      path: '/accept-invite',
      name: 'accept-invite',
      component: AcceptMemberInvitePage
    },

    // ------------------------------------------------------- Password Handling
    {
      path: '/password-reset',
      name: 'confirm-password-reset',
      component: ConfirmChangePasswordPage
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: ForgotPasswordPage
    },

    // ------------------------------------------------------------ Terms of Use
    {
      path: '/terms',
      name: 'terms-of-use',
      component: TermsOfUsePage
    },

    // ------------------------------------------------------------------- Login
    {
      path: '/login',
      name: 'login',
      component: LoginPage
    },

    // ----------------------------------------------------------- Oauth Handler
    {
      path: '/auth/callback',
      name: 'auth-callback-login',
      component: OAuthHandler,
      meta: { authLogin: true }
    },
    {
      path: '/auth/logout/callback',
      name: 'auth-callback-logout',
      component: OAuthHandler,
      meta: { authLogout: true }
    },

    // --------------------------------------------------------------- Home Page
    {
      path: '/home',
      name: 'home',
      component: HomePage,
      props: true,
      meta: { requiresAuth: true }
    },

    // ------------------------------------------------------------- Track Order
    {
      path: '/track/order/:trackingNumber?',
      name: 'track-order',
      component: TrackOrderPage,
      props: true
    },

    // --------------------------------------------------- Manage User Setttings
    {
      path: '/account/settings',
      component: UserSettingsRootPage,
      meta: { requiresAuth: true },
      children: [
        {
          path: 'profile',
          name: 'user-settings-profile',
          component: UserSettingsProfile
        },
        {
          path: 'facility-agent',
          name: 'user-settings-facility-agent',
          component: UserSettingsFacilityAgent
        },
        {
          path: 'driver',
          name: 'user-settings-driver',
          component: UserSettingsDriver
        },
        {
          path: '*',
          redirect: '/account/settings/profile'
        }
      ]
    },

    // --------------------------------------------- Download Invoice & Manifest
    {
      path: '/download/order/:orderId?',
      name: 'download-order-invoice',
      component: DownloadOrderInvoicePage,
      props: true
    },
    {
      path: '/download/route/:routeId?',
      name: 'download-route-manifest',
      component: DownloadRouteManifestPage,
      props: true
    },

    // ------------------------------------------------------ Manage Org Profile
    {
      path: '/org/profile',
      name: 'org-profile',
      component: OrgProfilePage,
      meta: { requiresAuth: true }
    },
    {
      path: '/org/profile/edit',
      name: 'org-profile-edit',
      component: OrgProfileEditPage,
      meta: {
        requiresAuth: true,
        ui_type: ['sys-admin', 'shipper']
      }
    },

    // ------------------------------------------------------ Manage Org Members
    {
      path: '/org/members',
      name: 'user-list',
      component: UserListPage,
      props: true,
      meta: { requiresAuth: true }
    },
    {
      path: '/org/member/:userId',
      component: UserRootPage,
      props: true,
      meta: { requiresAuth: true },
      children: [
        {
          path: '/',
          name: 'user-item',
          component: UserItemPage,
          props: true
        },
        {
          path: '*',
          redirect: '/org/members'
        }
      ]
    },

    // ----------------------------------------------- Manage Receiver Locations
    {
      path: '/receiver-location-list',
      name: 'receiver-list',
      component: ReceiverListPage,
      props: true,
      meta: {
        requiresAuth: true,
        ui_type: ['sys-admin', 'shipper']
      }
    },
    {
      path: '/receiver-license-history',
      name: 'receiver-license-history',
      component: ReceiverLicenseChangeHistoryPage,
      props: true,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/receiver-location/:receiverId',
      component: ReceiverRootPage,
      props: true,
      meta: { requiresAuth: true },
      children: [
        {
          path: '/',
          name: 'receiver-item',
          component: ReceiverItemPage,
          props: true
        },
        {
          path: 'edit-details',
          name: 'receiver-item-edit-details',
          component: ReceiverItemEditDetailsPage,
          props: true
        },
        {
          path: 'edit-licenses',
          name: 'receiver-item-edit-licenses',
          component: ReceiverItemEditLicensesPage,
          props: true
        },
        {
          path: '*',
          redirect: '/'
        }
      ]
    },

    // -------------------------------------------------------- Shipper UI Pages
    ...shipperUiRoutes,

    // ------------------------------------------------------- Receiver UI Pages
    ...receiverUiRoutes,

    // ------------------------------------------------------ Sys Admin UI Pages
    ...sysAdminUiRoutes,

    // ------------------------------------- (redirect unhandled routes to Home)
    {
      path: '/*', redirect: '/home'
    }
  ]
})

export default router
